import { createSlice } from '@reduxjs/toolkit';
import type {
  ActionCreatorWithPayload,
  ActionCreatorWithoutPayload,
  ActionReducerMapBuilder,
} from '@reduxjs/toolkit';

import type { JsonFailure, WsMessagePayload } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import { hasProperty, isNumber, isString } from '@skynet/type-guards';
import { prepareAccounts } from './utils';

import type {
  AccountView,
  AccountsState,
  JsonAccountError,
  JsonAccountDisableSuccess,
  JsonAccountEnableSuccess,
  JsonAccountsSuccess,
} from './types';
import { AccountStatus } from '@skynet/types-account-status';
import { AccountCommands } from './types';
import { disableAccount, enableAccount } from './actions';

registerErrorMessage(AccountCommands.Accounts, {
  title: 'Accounts Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

registerErrorMessage(AccountCommands.EnableAccount, {
  title: 'Account Status Update Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on updating account status. ${json.message}`,
});

registerErrorMessage(AccountCommands.DisableAccount, {
  title: 'Account Status Update Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on updating account status. ${json.message}`,
});

/**
 * Builds the initial accounts state.
 *
 * @param opts - Fields to override in the default (empty, not requested) state.
 */
export function initAccountsState(opts?: Partial<AccountsState>) {
  const initialState: AccountsState = {
    requestStatus: null,
    error: null,

    stats: [],
    list: [],
    brokenAccounts: [],

    resUpdateAccount: null,
  };

  return { ...initialState, ...opts };
}

function helperUpdateAccountState(updated: Partial<AccountView>) {
  return (account: AccountView) => {
    if (account.name === updated.name) {
      return {
        ...account,
        ...updated,
      };
    }
    return account;
  };
}

function isJsonAccountError(obj: unknown): obj is JsonAccountError {
  return (
    hasProperty(obj, 'name') &&
    isString(obj.name) &&
    hasProperty(obj, 'status') &&
    isNumber(obj.status) && // AccountStatus is a numeric enum, not a string
    hasProperty(obj, 'message') &&
    isString(obj.message) &&
    hasProperty(obj, 'label') &&
    isString(obj.label) &&
    hasProperty(obj, 'proxy') &&
    isString(obj.proxy)
  );
}

/**
 * Props for {@link initAccountsSlice}.
 */
export interface InitAccountsSliceProps {
  /** Log prefix, e.g. `shared` or `trading`. */
  prefix?: string;
  /** Redux slice name. */
  sliceName?: string;
  /** Optional pre-seeded state. */
  initialState?: AccountsState;
  /** Hook to register channel-specific reducers (e.g. the listen actions) on the builder. */
  extraReducers?: (builder: ActionReducerMapBuilder<AccountsState>) => void;
  /** Command map for the channel. */
  commands: typeof AccountCommands;
  /** The WS message action of the channel the slice consumes (MM or ES). */
  messages: ActionCreatorWithPayload<WsMessagePayload>;
  /**
   * Global reset action; when dispatched the slice returns to its initial state. The app owns
   * this action and injects it (the package must not depend on app-level store wiring).
   */
  resetAction: ActionCreatorWithoutPayload;
}

/**
 * Creates an accounts Redux reducer for a single WS channel. Handles the `Accounts`,
 * `EnableAccount` and `DisableAccount` commands and resets on {@link InitAccountsSliceProps.resetAction}.
 *
 * Returns the reducer; the write actions live in `accountsActions`.
 */
export function initAccountsSlice({
  prefix = 'shared',
  sliceName = 'accounts',
  initialState = initAccountsState(),
  extraReducers,
  commands,
  messages,
  resetAction,
}: InitAccountsSliceProps) {
  const allowedCommands = Object.values(commands);

  const accountsSlice = createSlice({
    name: sliceName,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
      // listen
      if (typeof extraReducers === 'function') {
        extraReducers(builder);
      }

      // enableAccount

      builder.addCase(enableAccount, (state, action) => {
        console.log(`${enableAccount.toString()}:`, action);
        state.resUpdateAccount = {
          id: action.payload.accountID,
          status: 'pending',
        };
      });

      // disableAccount

      builder.addCase(disableAccount, (state, action) => {
        console.log(`${disableAccount.toString()}:`, action.payload);
        state.resUpdateAccount = {
          id: action.payload.accountID,
          status: 'pending',
        };
      });

      // on WS message

      builder.addCase(messages, (state, action) => {
        const { json, skipProcessing, error, errorCommand } = parseResponse(
          action.payload,
          allowedCommands,
        );

        if (skipProcessing || !json) return;

        console.debug(
          `${prefix}:shared/accounts/processing ${messages.toString()}`,
          action,
          allowedCommands,
        );

        const { command, okCommand } = json;

        if (error) {
          state.error = error;

          switch (errorCommand) {
            case commands.EnableAccount:
              state.resUpdateAccount = null;
              break;

            case undefined:
            default:
          }

          return;
        }

        if (command === commands.Accounts) {
          const { accounts, errors }: JsonAccountsSuccess = json;
          if (!Array.isArray(accounts)) return;
          state.stats = accounts;

          // convert JsonAccountStats to AccountView
          state.list = prepareAccounts(accounts);
          state.brokenAccounts = errors || [];
          // state.isExchangeLoading = false;

          state.requestStatus = 'success';
          return;
        }

        if (isOkCommand(command) && okCommand === commands.EnableAccount) {
          const { result }: JsonAccountEnableSuccess = json;

          try {
            const parsedResult: unknown = JSON.parse(result);
            if (isJsonAccountError(parsedResult)) {
              const { name, status, message, label, proxy } = parsedResult;

              if (state.resUpdateAccount) {
                state.resUpdateAccount.status = 'success';
              }

              if (state.brokenAccounts.some((account) => account.name === name)) {
                state.brokenAccounts = state.brokenAccounts.map(
                  helperUpdateAccountState({ name, status, message, label, proxy }),
                );
              }
            } else {
              console.error('Parsed result does not match JsonAccountError type:', parsedResult);
            }
          } catch (err) {
            console.error(
              'Failed to parse response data for account status update request',
              result,
              err,
            );
          }
          console.debug(`${okCommand as string} accountID:`, json);
          return;
        }

        if (isOkCommand(command) && okCommand === commands.DisableAccount) {
          const { id }: JsonAccountDisableSuccess = json;

          state.resUpdateAccount!.status = 'success';

          if (state.brokenAccounts.some((account) => account.name === id)) {
            state.brokenAccounts = state.brokenAccounts.map(
              helperUpdateAccountState({ name: id, status: AccountStatus.Disabled }),
            );
          }
          console.debug(`${okCommand as string} accountID:`, json);
        }
      });

      builder.addCase(resetAction, () => {
        return initialState;
      });
    },
  });

  return accountsSlice.reducer;
}
